import { useState, useCallback, useMemo } from 'react';
import { UseMassPrintSelectionReturn } from './mass-print-types';

const MAX_SELECTIONS = 10;

/**
 * Custom hook for managing FDC task selection state for mass printing.
 * Handles selection/deselection logic with a maximum of 10 selections.
 *
 * @returns Selection state and helper methods
 *
 * @example
 * ```typescript
 * const {
 *   selectedTaskIds,
 *   toggleSelection,
 *   clearSelection,
 *   isSelected,
 *   canSelect,
 *   isMaxReached
 * } = useMassPrintSelection();
 *
 * // Toggle selection
 * toggleSelection(123);
 *
 * // Check if task is selected
 * if (isSelected(123)) {
 *   console.log('Task 123 is selected');
 * }
 *
 * // Clear all selections
 * clearSelection();
 * ```
 */
export const useMassPrintSelection = (): UseMassPrintSelectionReturn => {
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);

  /**
   * Toggle selection state for a given task ID.
   * Adds the task if not selected, removes it if already selected.
   * Will not add if maximum selections (10) is already reached.
   */
  const toggleSelection = useCallback((taskId: number) => {
    setSelectedTaskIds(prevSelected => {
      const isCurrentlySelected = prevSelected.includes(taskId);

      if (isCurrentlySelected) {
        // Remove from selection
        return prevSelected.filter(id => id !== taskId);
      }
      // Add to selection only if under max limit
      if (prevSelected.length < MAX_SELECTIONS) {
        return [...prevSelected, taskId];
      }
      // Already at max, return unchanged
      return prevSelected;
    });
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedTaskIds([]);
  }, []);

  /**
   * Check if a specific task ID is currently selected
   */
  const isSelected = useCallback(
    (taskId: number): boolean => {
      return selectedTaskIds.includes(taskId);
    },
    [selectedTaskIds],
  );

  /**
   * Check if more tasks can be selected (not at max limit)
   */
  const canSelect = useCallback((): boolean => {
    return selectedTaskIds.length < MAX_SELECTIONS;
  }, [selectedTaskIds.length]);

  /**
   * Computed value: whether maximum selections reached
   */
  const isMaxReached = useMemo(() => {
    return selectedTaskIds.length >= MAX_SELECTIONS;
  }, [selectedTaskIds.length]);

  /**
   * Computed value: current selection count
   */
  const selectedCount = useMemo(() => {
    return selectedTaskIds.length;
  }, [selectedTaskIds.length]);

  return {
    selectedTaskIds,
    toggleSelection,
    clearSelection,
    isSelected,
    canSelect,
    isMaxReached,
    selectedCount,
  };
};
